/**
 * rosterModel — pure, framework-free helpers for the board-first Shift Planner.
 * No React, no network: date math for the horizon window.
 *
 * There is no GenerateRosterResponse here any more. The solve is the planning
 * service's `POST /plans/roster/{id}:solve`, which writes what it found back as the
 * plan's items. The board reads the RESULT the same way it reads everything
 * else — as board cells, proposed from the plan or planned as shift rows.
 */

/** A planning window length, in days. Not a fixed union, because the board's
 *  horizon control is an option list and a longer one costs no type change. */
export type Horizon = number

/** The horizon options the board offers: week / fortnight / month. They are the
 *  board's own and are the only compiled-in values in this module — a control's
 *  option list is a frontend affordance, not planning policy, so no declaration
 *  carries them. */
export const DEFAULT_HORIZONS: readonly Horizon[] = [7, 14, 28]

/** Whether `value` is one of the currently-allowed (config-resolved) horizons. */
export function isHorizon(value: number, allowed: readonly number[]): boolean {
  return Number.isInteger(value) && allowed.includes(value)
}

/** Local `YYYY-MM-DD` for a Date (no UTC shift — the roster is day-granular). */
export function toIsoDate(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/** Today as `YYYY-MM-DD` in the local (ward) timezone. */
export function todayIso(): string {
  return toIsoDate(new Date())
}

/** Parse a `YYYY-MM-DD` to a local Date at noon (DST-safe for day math). */
export function parseIso(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1, 12)
}

/** `anchor` + n days, as an ISO date. */
export function addDays(iso: string, n: number): string {
  const d = parseIso(iso)
  d.setDate(d.getDate() + n)
  return toIsoDate(d)
}

/** The horizon window: `count` consecutive ISO days starting at `anchor`. */
export function windowDays(anchor: string, horizon: Horizon): string[] {
  return Array.from({ length: horizon }, (_, i) => addDays(anchor, i))
}

/** Human range label for the header, e.g. "12.–18. Juli" / "12 Jul – 8 Aug". */
export function formatRange(from: string, to: string, locale: string): string {
  const a = parseIso(from)
  const b = parseIso(to)
  const sameMonth = a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear()
  const start = a.toLocaleDateString(locale, sameMonth ? { day: 'numeric' } : { day: 'numeric', month: 'short' })
  const end = b.toLocaleDateString(locale, { day: 'numeric', month: 'short' })
  return `${start}–${end}`
}
